import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Header } from './Header'
import {
  LayoutDashboard, Users, Home, Sliders, ListFilter, Settings,
  BarChart3, ChevronRight, Tag, GitFork, X, LogOut, Box,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth-store'

const NAV = [
  { to: '/admin/dashboard',   icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/leads',       icon: Users,           label: 'Leads' },
  { to: '/admin/models',      icon: Home,            label: 'Models' },
  { to: '/admin/categories',  icon: Tag,             label: 'Categories' },
  { to: '/admin/options',     icon: Sliders,         label: 'Options' },
  { to: '/admin/constraints', icon: GitFork,         label: 'Constraints' },
  { to: '/admin/price-valve', icon: ListFilter,      label: 'Price Valve' },
  { to: '/admin/settings',    icon: Settings,        label: 'Settings' },
  { to: '/admin/analytics',   icon: BarChart3,       label: 'Analytics' },
  { to: '/admin/pipeline',    icon: Box,             label: 'Pipeline' },
]

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      {NAV.map(({ to, icon: Icon, label }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              isActive
                ? 'bg-brand-50 text-brand-700 font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </>
  )
}

interface Props {
  children: React.ReactNode
}

export function AdminLayout({ children }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { username, clearAuth } = useAuthStore()
  const navigate = useNavigate()

  function logout() {
    clearAuth()
    navigate('/admin/login', { replace: true })
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header variant="admin" onMenuToggle={() => setDrawerOpen(true)} />

      <div className="flex flex-1 overflow-hidden">
        {/* ── Desktop sidebar ────────────────────────────────────── */}
        <aside className="hidden md:flex flex-col w-56 bg-white border-r border-slate-200 shrink-0">
          <nav className="p-3 flex flex-col gap-0.5 flex-1">
            <NavItems />
          </nav>
          {/* Sidebar footer */}
          <div className="p-3 border-t border-slate-100">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-xs text-slate-400 truncate">{username}</span>
              <button
                onClick={logout}
                title="Sign out"
                className="text-slate-400 hover:text-red-500 transition-colors ml-2"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* ── Mobile drawer overlay ──────────────────────────────── */}
        {drawerOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setDrawerOpen(false)}
            />
            {/* Drawer panel */}
            <div className="relative w-72 bg-white flex flex-col shadow-xl">
              {/* Drawer header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 bg-brand-500 rounded-md flex items-center justify-center">
                    <span className="text-white font-bold text-xs">L&M</span>
                  </div>
                  <span className="font-semibold text-slate-800 text-sm">Admin</span>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Nav items */}
              <nav className="p-3 flex flex-col gap-0.5 flex-1 overflow-y-auto">
                <NavItems onNavigate={() => setDrawerOpen(false)} />
              </nav>

              {/* Drawer footer */}
              <div className="p-3 border-t border-slate-100">
                <button
                  onClick={logout}
                  className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
                <p className="text-xs text-slate-400 px-3 mt-2">Signed in as {username}</p>
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  )
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}

export function Breadcrumb({ items }: { items: Array<{ label: string; to?: string }> }) {
  return (
    <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-6">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5" />}
          {item.to ? (
            <NavLink to={item.to} className="hover:text-slate-900 transition-colors">
              {item.label}
            </NavLink>
          ) : (
            <span className="text-slate-900 font-medium">{item.label}</span>
          )}
        </div>
      ))}
    </nav>
  )
}
